import { useState, useEffect } from 'react';
import {
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  signInAnonymously,
  signOut
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

export const useAuth = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userId, setUserId] = useState(null);
  const [userDisplayName, setUserDisplayName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    if (!auth) {
      setAuthError("Firebase Auth not initialized. Please check your configuration.");
      setIsAuthLoading(false);
      setIsAuthReady(true);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        setUserId(user.uid);
        setUserDisplayName(
          user.displayName || (user.isAnonymous ? "Anonymous User" : "User")
        );
        setUserEmail(user.email || "");

        // Lazy profile creation/update - stored on user document directly
        if (db) {
          try {
            const userDocRef = doc(db, `artifacts/bz-tracker/users/${user.uid}`);
            const userDocSnap = await getDoc(userDocRef);

            if (!userDocSnap.exists()) {
              // Create new user document for first-time users
              await setDoc(userDocRef, {
                isAdmin: false,
                role: "user",
                createdAt: serverTimestamp(),
                lastLoginAt: serverTimestamp()
              });
            } else {
              // Update lastLoginAt, and add missing profile fields if needed
              const userData = userDocSnap.data();
              const updates = { lastLoginAt: serverTimestamp() };

              // Add missing profile fields for existing users
              if (userData.isAdmin === undefined) updates.isAdmin = false;
              if (userData.role === undefined) updates.role = "user";
              if (userData.createdAt === undefined) updates.createdAt = serverTimestamp();

              await updateDoc(userDocRef, updates);
            }
          } catch (profileError) {
            console.error("Error managing user profile:", profileError);
            // Don't block auth flow if profile creation fails
          }
        }
      } else {
        setCurrentUser(null);
        setUserId(null);
        setUserDisplayName("");
        setUserEmail("");
      }
      setIsAuthLoading(false);
      setIsAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  const handleGoogleSignIn = async () => {
    if (!auth) {
      setAuthError("Firebase Auth not ready for Google Sign-In.");
      return;
    }
    const provider = new GoogleAuthProvider();
    try {
      setAuthError(null);
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Google Sign-In Error:", error);
      setAuthError(`Google Sign-In Failed: ${error.message}`);
    }
  };

  const handleSignInAnonymously = async () => {
    if (!auth) {
      setAuthError("Firebase Auth not ready for Anonymous Sign-In.");
      return;
    }
    try {
      setAuthError(null);
      await signInAnonymously(auth);
    } catch (error) {
      console.error("Anonymous Sign-In Error:", error);
      setAuthError(`Anonymous Sign-In Failed: ${error.message}`);
    }
  };

  const handleSignOut = async () => {
    if (!auth) {
      setAuthError("Firebase Auth not ready for Sign-Out.");
      return;
    }
    try {
      setAuthError(null);
      await signOut(auth);
    } catch (error) {
      console.error("Sign Out Error:", error);
      setAuthError(`Sign Out Failed: ${error.message}`);
    }
  };

  return {
    currentUser,
    userId,
    userDisplayName,
    userEmail,
    isAuthLoading,
    isAuthReady,
    authError,
    handleGoogleSignIn,
    handleSignInAnonymously,
    handleSignOut,
  };
};